<template>
    <span>
        <div class="flex flex-col w-full h-52 items-center justify-center splash-screen-loading-box" v-if="loading">
            <ProgressSpinner />
        </div>
        <div v-else class="page">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Left Column -->
                <div class="flex flex-col gap-6">
                    <!-- Ranking title (shown on public ranking view) -->
                    <div class="flex flex-col gap-2">
                        <FieldLabelWithTooltip
                            wrapper-class="text-sm font-medium"
                            :label="$t('gm.collabo.ranking_title')"
                            :tooltip="$t('gm.collabo.ranking_title_tooltip')"
                        />
                        <InputText
                            v-model="(storeEvents.activityConfiguration as any).title"
                            :placeholder="$t('gm.collabo.ranking_title_placeholder')"
                            class="w-full"
                        />
                    </div>

                    <!-- Preset Selection -->
                    <div class="flex flex-col gap-2">
                        <label class="text-sm font-medium">{{ $t('gm.dashboards.type') }}</label>
                        <Dropdown
                            v-model="(storeEvents.activityConfiguration as any).preset"
                            :options="settingsOptions"
                            optionLabel="label"
                            optionValue="value"
                            :placeholder="$t('gm.dashboards.placeholder_type')"
                            :class="['w-full', { 'p-invalid': errors.preset }]"
                            @change="onPresetChange"
                        />
                        <small v-if="errors.preset" class="p-error">{{ errors.preset }}</small>
                    </div>

                    <!-- Materials Section -->
                    <div v-if="selectedPreset" class="flex flex-col gap-3">
                        <h3 class="text-lg font-semibold">{{ $t('gm.collabo.materials') }}</h3>
                        <div class="grid grid-cols-2 gap-2">
                            <div
                                v-for="material in selectedMaterials"
                                :key="material"
                                class="p-3 border rounded-lg bg-card flex items-center gap-3"
                            >
                                <img 
                                    v-if="getMaterialImage(material)" 
                                    :src="getMaterialImage(material)" 
                                    :alt="getTranslatedObject(selectedPreset, `material_${material}_name`)"
                                    class="w-12 h-12 object-cover rounded"
                                />
                                <div class="font-medium">{{ getTranslatedObject(selectedPreset, `material_${material}_name`) }}</div>
                            </div>
                            <div v-if="selectedMaterials.length === 0" class="text-gray-500">
                                {{ $t('gm.collabo.no_materials') }}
                            </div>
                        </div>
                    </div>

                    <!-- Constructions Section -->
                    <div v-if="selectedPreset" class="flex flex-col gap-3">
                        <h3 class="text-lg font-semibold">{{ $t('gm.collabo.constructions') }}</h3>
                        <div class="grid grid-cols-2 gap-2">
                            <div
                                v-for="construction in selectedConstructions"
                                :key="construction"
                                class="p-3 border rounded-lg bg-card flex items-center gap-3"
                            >
                                <img 
                                    v-if="getConstructionImage(construction)" 
                                    :src="getConstructionImage(construction)" 
                                    :alt="getTranslatedObject(selectedPreset, `construction_${construction}_name`)"
                                    class="w-12 h-12 object-cover rounded"
                                />
                                <div class="font-medium">{{ getTranslatedObject(selectedPreset, `construction_${construction}_name`) }}</div>
                            </div>
                            <div v-if="selectedConstructions.length === 0" class="col-span-2 text-gray-500">
                                {{ $t('gm.collabo.no_constructions') }}
                            </div>
                        </div>
                    </div>

                    <!-- Challenges Section -->
                    <div v-if="selectedPreset" class="flex flex-col gap-3">
                        <h3 class="text-lg font-semibold">{{ $t('gm.collabo.challenges') }}</h3>
                        <div class="flex flex-col gap-2">
                            <div
                                v-for="challenge in selectedChallenges"
                                :key="challenge.id"
                                class="p-3 border rounded-lg bg-card"
                            >
                                <div class="font-medium">{{ getTranslatedObject(challenge, 'name') }}</div>
                            </div>
                            <div v-if="selectedChallenges.length === 0" class="text-gray-500">
                                {{ $t('gm.collabo.no_challenges') }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right Column -->
                <div class="flex flex-col gap-6">

                    <!-- Time Configuration -->
                    <div v-if="selectedPreset" class="flex flex-col gap-4">
                        <!-- Round Time -->
                        <div class="flex flex-col gap-2">
                            <label class="text-sm font-medium">{{ $t('gm.collabo.round_time') }}</label>
                            <InputNumber
                                v-model="roundTimeMinutes"
                                :min="0"
                                :max="999"
                                :placeholder="$t('gm.collabo.round_time_placeholder')"
                                :class="['w-full', { 'p-invalid': errors.time_challenge_round }]"
                                @update:modelValue="updateRoundTime"
                            />
                            <small v-if="errors.time_challenge_round" class="p-error">{{ errors.time_challenge_round }}</small>
                        </div>

                        <!-- Challenge Time -->
                        <div class="flex flex-col gap-2">
                            <label class="text-sm font-medium">{{ $t('gm.collabo.challenge_time') }}</label>
                            <InputNumber
                                v-model="challengeTimeMinutes"
                                :min="0"
                                :max="999"
                                :placeholder="$t('gm.collabo.challenge_time_placeholder')"
                                :class="['w-full', { 'p-invalid': errors.time_construction_round }]"
                                @update:modelValue="updateChallengeTime"
                            />
                            <small v-if="errors.time_construction_round" class="p-error">{{ errors.time_construction_round }}</small>
                        </div>
                    </div>

                    <!-- Game Mode: Teams vs Individual -->
                    <div v-if="selectedPreset" class="flex flex-col gap-2">
                        <label class="text-sm font-medium">{{ $t('gm.collabo.game_mode') }}</label>
                        <div class="flex flex-wrap gap-4" :class="{ 'p-invalid': errors.game_mode }">
                            <div
                                v-for="mode in gameModeOptions"
                                :key="mode.value"
                                class="flex items-center gap-1"
                            >
                                <RadioButton
                                    :inputId="'collabo_mode_' + mode.value"
                                    :value="mode.value"
                                    v-model="(storeEvents.activityConfiguration as any).game_mode"
                                    @update:modelValue="() => clearError('game_mode')"
                                />
                                <label :for="'collabo_mode_' + mode.value" class="ml-2 cursor-pointer text-sm font-medium">
                                    {{ mode.label }}
                                </label>
                                <i
                                    v-if="mode.tooltip"
                                    class="pi pi-question-circle text-sm text-primary cursor-help shrink-0"
                                    v-tooltip.top="{
                                        value: mode.tooltip,
                                        showDelay: 200,
                                        hideDelay: 0,
                                        tooltipStyleClass: 'max-w-md whitespace-normal break-words',
                                    }"
                                    @click.stop.prevent
                                    @mousedown.stop.prevent
                                    @pointerdown.stop
                                />
                            </div>
                        </div>
                        <small v-if="errors.game_mode" class="p-error">{{ errors.game_mode }}</small>
                    </div>
                </div>
            </div>
        </div>
    </span>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue"
import { useRoute } from "vue-router"
import { useEventsStore } from "@/stores/apps/events"
import { fetchPromiseAll } from "@/utils/http"
import { useContentStore } from "@/stores/apps/content"
import { useStoreI18n } from "@/composables/useStoreI18n"
import { useCollaboStore } from "@/stores/apps/collabo"
import Dropdown from "primevue/dropdown"
import InputText from "primevue/inputtext"
import InputNumber from "primevue/inputnumber"
import RadioButton from "primevue/radiobutton"
import ProgressSpinner from "primevue/progressspinner"
import FieldLabelWithTooltip from "@/components/common/FieldLabelWithTooltip.vue"
import { getTranslatedObject } from "@/utils/translate"

const { t, getLocale } = useStoreI18n()
const storeEvents = useEventsStore()
const storeContent = useContentStore()
const storeCollabo = useCollaboStore()

const route = useRoute()
const loading = ref(true)
const settingsOptions = ref<Array<{ label: string; value: number }>>([])
const selectedPreset = ref<any>(null)
const roundTimeMinutes = ref<number | null>(null)
const challengeTimeMinutes = ref<number | null>(null)
const errors = ref<Record<string, string>>({})

const gameModeOptions = [
    { label: t('gm.collabo.teams'), value: 0, tooltip: t('gm.collabo.teams_tooltip') },
    { label: t('gm.collabo.individual'), value: 1, tooltip: t('gm.collabo.individual_tooltip') },
]

// Computed properties for selected items
const selectedMaterials = computed(() => {
    if (!selectedPreset.value) return []
    // Materials are always 1-4 in collabo presets
    return [1, 2, 3, 4]
})

const selectedConstructions = computed(() => {
    if (!selectedPreset.value) return []
    // Constructions are always 1-2 in collabo presets
    return [1, 2,3,4,5,6,7]
})

const selectedChallenges = computed(() => {
    if (!selectedPreset.value || !selectedPreset.value.challenge) return []
    const challenges = (storeContent.content as any).collabo_challenge
    return challenges?.filter((ch: any) =>
        selectedPreset.value.challenge.includes(ch.id)
    ) || []
})

const getMaterialImage = (materialId: number) => {
    if (!selectedPreset.value) return null
    const key = `material_${materialId}_image`
    return selectedPreset.value[key] || null
}

const getConstructionImage = (constructionId: number) => {
    if (!selectedPreset.value) return null
    const key = `construction_${constructionId}_image`
    return selectedPreset.value[key] || null
}

// Clear error for a specific field
const clearError = (fieldName: string) => {
    if (errors.value[fieldName]) {
        delete errors.value[fieldName]
    }
}

// Validation function
const validateForm = (callback: (errors: any) => void) => {
    const config = storeEvents.activityConfiguration as any
    const validationErrors: any = {}
    
    // Clear previous errors
    errors.value = {}
    
    if (!config.preset || config.preset === "") {
        const errorMsg = t('gm.dashboards.placeholder_template')
        validationErrors.preset = errorMsg
        errors.value.preset = errorMsg
    }
    
    if (config.game_mode === null || config.game_mode === "") {
        const errorMsg = t('gm.collabo.game_mode') + ' ' + t('general.must')
        validationErrors.game_mode = errorMsg
        errors.value.game_mode = errorMsg
    }
    
    if (!config.time_challenge_round || config.time_challenge_round === 0) {
        const errorMsg = t('gm.collabo.round_time') + ' ' + t('general.must')
        validationErrors.time_challenge_round = errorMsg
        errors.value.time_challenge_round = errorMsg
    }
    
    if (!config.time_construction_round || config.time_construction_round === 0) {
        const errorMsg = t('gm.collabo.challenge_time') + ' ' + t('general.must')
        validationErrors.time_construction_round = errorMsg
        errors.value.time_construction_round = errorMsg
    }
    
    callback(Object.keys(validationErrors).length > 0 ? validationErrors : null)
}

// Create form ref object with validate method
const createFormRef = () => {
    return {
        validate: validateForm
    }
}

// Initialize activity configuration
onMounted(async () => {
    storeEvents.activityConfiguration = {
        title: "",
        preset: "",
        game_mode: 1,
        activity_formRef: createFormRef(),
        time_challenge_round: 0, // in seconds
        time_construction_round: 0, // in seconds
    }

    const promises = [
        storeContent.getAllMultipleContent(["collabo_material"]),
        storeContent.getAllMultipleContent(["collabo_construction_game"]),
        storeContent.getAllMultipleContent(["collabo_preset"]),
        storeContent.getAllMultipleContent(["collabo_challenge"]),
    ]

    if (route.params.api_key) {
        promises.push(storeCollabo.fetchCollaboData(String(route.params.api_key)))
    }

    await fetchPromiseAll(promises)
    const presets = (storeContent.content as any).collabo_preset
    settingsOptions.value = presets?.map((v: any) => ({
        label: v.name,
        value: v.id.toString()
    })) || []

    // Load existing data if editing
    if (route.params.api_key && storeCollabo.collaboData) {
        loadCollaboForm()
    }
    
    // Set form ref after initialization
    (storeEvents.activityConfiguration as any).activity_formRef = createFormRef()

    loading.value = false
})

// Load form data when editing
const loadCollaboForm = () => {
    const data = storeCollabo.collaboData as any
    if (data.title != null && data.title !== undefined) {
        (storeEvents.activityConfiguration as any).title = data.title
    }
    if (data.preset) {
        (storeEvents.activityConfiguration as any).preset = data.preset.id.toString()
        onPresetChange()
    }
    if (data.time_challenge_round) {
        roundTimeMinutes.value = data.time_challenge_round / 60
        ;(storeEvents.activityConfiguration as any).time_challenge_round = data.time_challenge_round
    }
    if (data.time_construction_round) {
        challengeTimeMinutes.value = data.time_construction_round / 60
        ;(storeEvents.activityConfiguration as any).time_construction_round = data.time_construction_round
    }
    if (data.game_mode !== null && data.game_mode !== undefined) {
        (storeEvents.activityConfiguration as any).game_mode = data.game_mode
    }
}

// Handle preset change
const onPresetChange = () => {
    const presetId = (storeEvents.activityConfiguration as any).preset
    clearError('preset')
    if (presetId) {
        const presets = (storeContent.content as any).collabo_preset
        selectedPreset.value = presets?.find(
            (p: any) => p.id.toString() === presetId.toString()
        )
    } else {
        selectedPreset.value = null
    }
}

// Update round time (convert minutes to seconds)
const updateRoundTime = (minutes: number | null) => {
    clearError('time_challenge_round')
    if (minutes !== null) {
        (storeEvents.activityConfiguration as any).time_challenge_round = minutes * 60
    } else {
        (storeEvents.activityConfiguration as any).time_challenge_round = 0
    }
}

// Update challenge time (convert minutes to seconds)
const updateChallengeTime = (minutes: number | null) => {
    clearError('time_construction_round')
    if (minutes !== null) {
        (storeEvents.activityConfiguration as any).time_construction_round = minutes * 60
    } else {
        (storeEvents.activityConfiguration as any).time_construction_round = 0
    }
}

// Watch for preset changes
watch(() => (storeEvents.activityConfiguration as any).preset, (newVal) => {
    if (newVal) {
        onPresetChange()
    }
})
</script>

<style lang="scss" scoped>
.components-list {
    grid-template-columns: none;
}
.card-css {
    background-color: var(--bg-color);
}
.bg-card {
    background-color: var(--bg-color);
}

.p-error {
    color: #e24c4c;
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

:deep(.p-invalid) {
    border-color: #e24c4c !important;
}
</style>
